import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TodoPageAllocationTasksRedirectQuery } from '@/__generated__/graphql';
import { getCacheBustedFileUrl } from '@/components/settings-page/utils';
import { useAuth } from '@/hooks/use-auth';
import { SidebarTabs } from '@/shared/types/sidebarTabs';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult } from '@apollo/client';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';
import packageJson from '../../../package.json';
import { useCustomizationsData } from '../../hooks/useCustomizationsData';

const logoAnimationVariants = {
  initial: { opacity: 0, scale: 0.5, y: -5 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.5, y: -5 }
};

const logoTransition = {
  duration: 0.35
};

interface DashboardNavbarProps {
  tabs: SidebarTabs[];
  menuConfig: { [key: string]: any };
  allocationTasksRefetch: () => Promise<ApolloQueryResult<TodoPageAllocationTasksRedirectQuery>>;
}

export const DashboardNavbar = ({ tabs, menuConfig, allocationTasksRefetch }: DashboardNavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userTasks } = useAuth();

  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isMouseInside, setIsMouseInside] = useState(false);
  const [isNavFixed, setIsNavFixed] = useState(false);
  const [isToDoActive, setIsToDoActive] = useState(false);

  const { data: customizationsData } = useCustomizationsData();

  const mouseRef = useRef(isMouseInside);
  mouseRef.current = isMouseInside;

  useEffect(() => {
    if (mouseRef.current) {
      setTimeout(() => {
        if (mouseRef.current) {
          setIsNavOpen(true);
        }
      }, 1200);
    }
  }, [isMouseInside]);

  useEffect(() => {
    setIsToDoActive(!!location.pathname.includes('/todo'));
  }, [location.pathname]);

  const onMouseLeave = () => {
    setIsMouseInside(false);
    setIsNavOpen(false);
  };

  const onLogoClick = () => {
    setIsNavFixed(!isNavFixed);
    setIsNavOpen(!isNavOpen);
  };

  const selectedTabIndex = tabs.findIndex((tab) => location.pathname.includes(tab.path.split('/')[0])) ?? 0;
  const isNavExpanded = isNavOpen || isNavFixed;

  const navbarLogoUrl = getCacheBustedFileUrl(customizationsData?.logos?.navbarLogo || process.env.REACT_APP_NAVBAR_LOGO);
  const navbarSimpleLogoUrl = getCacheBustedFileUrl(customizationsData?.logos?.navbarSimpleLogo || process.env.REACT_APP_NAVBAR_SIMPLE_LOGO);

  return (
    <NavBarWrap
      onMouseEnter={() => {
        setIsMouseInside(true);
      }}
      onMouseLeave={onMouseLeave}
    >
      <DashboardNavbarWrap isNavOpen={isNavExpanded}>
        <LogoWrap>
          <AnimatePresence>
            {isNavExpanded ? (
              <NavbarLogoWrapper key="navbar-logo" {...logoAnimationVariants} transition={logoTransition}>
                <NavbarLogoImg src={navbarLogoUrl} onClick={onLogoClick} />
              </NavbarLogoWrapper>
            ) : (
              <NavbarSimpleLogoWrapper key="navbar-simple-logo" {...logoAnimationVariants} transition={logoTransition}>
                <NavbarSimpleLogoImg src={navbarSimpleLogoUrl} onClick={onLogoClick} />
              </NavbarSimpleLogoWrapper>
            )}
          </AnimatePresence>
        </LogoWrap>
        {/* {menuConfig?.todo && (
          <ToDoWrap
            active={isToDoActive}
            isNavOpen={isNavOpen}
            onClick={() => {
              setIsToDoActive(true);
              navigate('/todo');
              allocationTasksRefetch();
            }}
          >
            <NumberWrap isNavOpen={isNavOpen}>
              <TodoCalendar />
              <ToDoNumber>{userTasks}</ToDoNumber>
            </NumberWrap>
            <ToDoTitle isNavOpen={isNavOpen}>TO DO</ToDoTitle>
          </ToDoWrap>
        )} */}
        <MenuWrap>
          {tabs?.map((el, index) => {
            return (
              !el?.disabled && (
                <MenuItemWrap
                  active={index === selectedTabIndex}
                  onClick={(event) => {
                    event.preventDefault();
                    if (location.pathname === `/${el.path}` && location.pathname !== '/settings') return;

                    const returnUrl = location.state?.returnUrl || `/${el.path}`;
                    navigate(returnUrl);

                    setIsNavOpen(false);
                    setIsNavFixed(false);
                  }}
                  key={el.path}
                >
                  {el.icon}
                  <MenuItemTitle>{el.label}</MenuItemTitle>
                </MenuItemWrap>
              )
            );
          })}
        </MenuWrap>
        <MenuFooter>{packageJson.version}</MenuFooter>
      </DashboardNavbarWrap>
    </NavBarWrap>
  );
};

const NavBarWrap = styled.div`
  display: flex;
  position: fixed;
  transition: 0.3s;
  z-index: 1001;
`;

const MenuFooter = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: auto;
`;

const displayToDoTitle = keyframes`
  to {
    visibility: visible;
  }
`;

const MenuItemTitle = styled.div`
  font-family: Lato;
  color: ${themeVar('header.action')};
  font-weight: 700;
  white-space: nowrap;
  animation: 0s linear 0.1s forwards ${displayToDoTitle};
`;

const MenuItemWrap = styled.div<{ active: boolean }>`
  cursor: pointer;
  background-color: ${({ active }) => (active ? themeVar('menu.active') : 'transparent')};
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 30px;
  padding: 20px 25px;

  svg {
    path {
      fill: ${themeVar('header.action')};
    }
  }
`;

const ToDoWrap = styled.div<{ isNavOpen: boolean; active?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${({ active }) => (active ? themeVar('menu.active') : 'transparent')};
  border-radius: 10px;
  padding: ${({ isNavOpen }) => (isNavOpen ? '20px' : '18px 16px')};
  transition: 0.3s;
  max-height: 100px;
  height: 100%;
  cursor: pointer;
`;

const flexDirectionRow = keyframes`
  to {
    flex-direction: row;
  }
`;

const todoTitleFontSize = keyframes`
  to {
    font-size: 16px;
  }
`;

const NumberWrap = styled.div<{ isNavOpen: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  animation: ${({ isNavOpen }) => (isNavOpen ? css`0s linear .1s forwards ${flexDirectionRow}` : 'none')};
  transition: 0.3s;
`;

const ToDoTitle = styled.div<{ isNavOpen: boolean }>`
  font-family: Lato;
  font-weight: 700;
  font-size: 0;
  color: ${themeVar('header.action')};
  animation: ${({ isNavOpen }) => (isNavOpen ? css`0s linear .1s forwards ${todoTitleFontSize}` : 'none')};
`;

const ToDoNumber = styled.div`
  font-family: Lato;
  font-size: 23px;
  color: ${themeVar('header.action')};
  font-weight: 700;
`;

const MenuWrap = styled.div`
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  margin: 0 -10px;

  &::-webkit-scrollbar {
    display: none;
    background-color: transparent;
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    display: none;
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${themeVar('layer.4')};
    border-radius: 6px;
    border: 2px solid transparent;
  }

  &::-webkit-scrollbar-button {
    display: none;
  }
`;

const LogoWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 28px;
  min-height: 64px;
  position: relative;
`;

const NavbarLogoWrapper = styled(motion.div)`
  width: 168px;
  height: 40px;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NavbarLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const NavbarSimpleLogoWrapper = styled(motion.div)`
  width: 40px;
  height: 40px;
  padding: 3px;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NavbarSimpleLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const DashboardNavbarWrap = styled.div<{ isNavOpen: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  height: 100vh;
  position: ${({ isNavOpen }) => (isNavOpen ? 'absolute' : 'fixed')};
  width: ${({ isNavOpen }) => (isNavOpen ? '270px' : '80px')};
  background-color: ${themeVar('menu.tab')};
  transition: 0.3s;
  padding: 20px 10px;
  padding-bottom: 0;
  z-index: 400;
  top: 0;
  bottom: 0;
  left: 0;
  box-shadow: 4px 0px 8px 0px rgba(0, 0, 0, 0.25);
`;
